import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import instantsearch from 'instantsearch.js';
import {
    currentRefinements,
    panel,
    refinementList,
    clearRefinements,
    configure,
    menuSelect,
    infiniteHits,
    stats,
} from 'instantsearch.js/es/widgets';
import { geohashWidget } from './search/widgets/geohash';
import { translations } from './search/translations';
import refinementListTemplates from './search/templates/refinementList';
import statsTemplates from './search/templates/stats';
import infiniteHitsTemplates from './search/templates/infiniteHits';
import panelTemplates from './search/templates/panel';
import { searchBoxWithLabelWidget } from './search/widgets/searchBoxWithLabel';
import { eventTrackingMiddlewareFactory } from './search/event-tracking-middleware';

// toggle filters on mobile
const toggleFiltersButton = document.getElementById('search-page-filters-toggle');
const filters = document.getElementById('search-page-filters');
if (toggleFiltersButton && filters) {
    const showFiltersText = toggleFiltersButton.dataset.showText;
    const hideFiltersText = toggleFiltersButton.dataset.hideText;
    toggleFiltersButton.addEventListener('click', e => {
        const isExpanded = toggleFiltersButton.getAttribute('aria-expanded') === 'true';
        const nextState = isExpanded ? 'false' : 'true';
        toggleFiltersButton.setAttribute('aria-expanded', nextState);
        toggleFiltersButton.innerText = isExpanded ? showFiltersText : hideFiltersText;
    });
}

const { url = null, apiKeySearch = null } = window.MeiliSearchConfig;
const indexName = 'search';

const { searchClient } = instantMeiliSearch(url, apiKeySearch, {
    primaryKey: 'id',
});

const search = instantsearch({
    indexName,
    searchClient,
    routing: true,
    future: {
        preserveSharedStateOnUnmount: false,
    },
});

const refinementListDefaults = {
    limit: 5,
    showMore: true,
    templates: {
        showMoreText: refinementListTemplates.showMoreText,
        item: refinementListTemplates.item,
    },
};

// utility function to check if an entry type filter is currently active
const hasEntryTypeFilter = state =>
    (state.hierarchicalFacetsRefinements.entry_type ?? []).length > 0;

// list of category facet attributes for each entry type with a main category
const mainCategoryFacets = [
    'type_institute',
    'type_beratungsangebote',
    'type_foerderangebote',
    'type_medien',
    'type_plattformen',
    'type_veranstaltungen',
    'type_news',
];

// create a refinementList widget for each of the main category facets that is
// only visible if the corresponding entry_type is currently selected
const mainCategoryRefinementWidgets = mainCategoryFacets.map(name =>
    panel({
        templates: {
            header: (_, { html }) =>
                panelTemplates.header(translations.mainCategoryListHeadline, html),
        },
        hidden: options => !hasEntryTypeFilter(options.state) || !options.canRefine,
    })(refinementList)({
        ...refinementListDefaults,
        container: `[data-widget="${name}"]`,
        limit: 10,
        attribute: name,
    }),
);

// the modality refinement is only visible if an entry_type with that field is active
const modalityRefinementList = panel({
    hidden: options => !hasEntryTypeFilter(options.state) || !options.canRefine,
    templates: {
        header: (_, { html }) => panelTemplates.header(translations.filterLabels.modality, html),
    },
})(refinementList)({
    container: '[data-widget="modality"]',
    attribute: 'modality',
    showMore: false,
});

// middleware: when the entry_type is reset, clear the active main category and modality filters
const clearCategoryOnEntryTypeChangeMiddleware = ({ instantSearchInstance }) => {
    let previousUiState;
    return {
        onStateChange({ uiState }) {
            // don't do anything for page changes, modifying the state will reset the page number
            const previousPage = previousUiState?.[indexName]?.page;
            const nextPage = uiState[indexName]?.page;
            if (nextPage !== previousPage) return;

            const entryTypeFilterIsActive = hasEntryTypeFilter(instantSearchInstance.helper.state);
            if (!entryTypeFilterIsActive) {
                instantSearchInstance.helper.clearRefinements(function (value, attribute, type) {
                    return mainCategoryFacets.includes(attribute) || attribute === 'modality';
                });
                instantSearchInstance.helper.search();
            }
            previousUiState = uiState;
        },
        subscribe() {},
        unsubscribe() {},
    };
};

search.use(clearCategoryOnEntryTypeChangeMiddleware);

// add event tracking for search interactions
const eventTrackingMiddleware = eventTrackingMiddlewareFactory(
    indexName,
    '[data-widget="results"]',
);
search.use(eventTrackingMiddleware);

search.addWidgets([
    configure({
        hitsPerPage: 10,
    }),

    // Search box, current filters & reset button
    searchBoxWithLabelWidget({
        container: '[data-widget="search-input"]',
        label: translations.searchBoxLabel,
        placeholder: translations.searchBoxPlaceholder,
        showSubmit: false,
        submitAriaLabel: translations.searchBoxSubmitAriaLabel,
    }),
    geohashWidget({
        container: '[data-widget="geohash-widget"]',
        searchParameters: {},
        url,
        apiKey: apiKeySearch,
    }),
    currentRefinements({
        container: '[data-widget="current-refinements"]',
        transformItems: items => {
            const labelledItems = items.map(item => ({
                ...item,
                label: translations.filterLabels[item.label] ?? item.label,
            }));
            const order = Object.keys(translations.filterLabels);
            return labelledItems.sort((a, b) => {
                return order.indexOf(a.attribute) - order.indexOf(b.attribute);
            });
        },
    }),
    stats({
        container: '[data-widget="stats"]',
        templates: {
            text: statsTemplates.text,
        },
    }),
    panel({
        hidden: options => !options.hasRefinements,
    })(clearRefinements)({
        container: '[data-widget="clear-refinements"]',
        templates: {
            resetLabel({ hasRefinements }, { html }) {
                return `${translations.clearRefinementsResetLabel}`;
            },
        },
    }),

    // Results & pagination
    infiniteHits({
        container: '[data-widget="results"]',
        templates: {
            item: infiniteHitsTemplates.item,
            empty: infiniteHitsTemplates.empty,
            showMoreText: infiniteHitsTemplates.showMoreText,
        },
    }),

    // Entry type filter with main category filters, modality options and specific reset button
    panel({
        templates: {
            header: (_, { html }) =>
                panelTemplates.header(translations.filterLabels.entry_type, html),
        },
    })(menuSelect)({
        container: '[data-widget="entry_type"]',
        attribute: 'entry_type',
        limit: 100, // Always show all entry types
        cssClasses: {
            select: ['form-control-secondary', 'form-select-sm'],
        },
        templates: {
            defaultOption(data, { html }) {
                return html`${translations.menuSelectSeeAllLabel}`;
            },
        },
    }),
    ...mainCategoryRefinementWidgets,
    modalityRefinementList,
    panel({
        hidden: options => !options.hasRefinements,
    })(clearRefinements)({
        container: '[data-widget="clear-entry-type"]',
        includedAttributes: ['entry_type'],
        templates: {
            resetLabel({ hasRefinements }, { html }) {
                return `${translations.mainCategoryListResetLabel}`;
            },
        },
    }),

    // other taxonomies (subject, topic, audience)
    panel({
        hidden: options => !options.canRefine,
        templates: {
            header: (_, { html }) =>
                panelTemplates.header(translations.filterLabels.audience, html),
        },
    })(refinementList)({
        container: '[data-widget="audience"]',
        attribute: 'audience',
        ...refinementListDefaults,
    }),
    panel({
        hidden: options => !options.canRefine,
        templates: {
            header: (_, { html }) => panelTemplates.header(translations.filterLabels.subject, html),
        },
    })(refinementList)({
        container: '[data-widget="subject"]',
        attribute: 'subject',
        ...refinementListDefaults,
    }),
    panel({
        hidden: options => !options.canRefine,
        templates: {
            header: (_, { html }) => panelTemplates.header(translations.filterLabels.topic, html),
        },
    })(refinementList)({
        container: '[data-widget="topic"]',
        attribute: 'topic',
        ...refinementListDefaults,
    }),
]);

search.start();

// Fix missing aria label / label association for entry type filter
search.on('render', () => {
    const select = document.querySelector('[data-widget="entry_type"] .ais-MenuSelect-select');
    if (select) {
        select.setAttribute('aria-label', translations.filterLabels.entry_type);
    }
});
